import React from "react";

import ModalProPromo from "./ModalProPromo";
import QuickCapture from "./QuickCapture";

// Quick Capture plus its Pro gating: the real thing for subscribers, the promo and a
// scripted demo for everyone else.
//
// Extracted from QuickCaptureModal so the gating isn't tied to being in a modal —
// In the Wild renders capture inline beside the video, and without this it would either
// lose the promo or carry a second copy of it.
//
// Deliberately a wrapper rather than logic inside QuickCapture itself. Two reasons:
// QuickCapture renders *itself* to run the demo, so a non-Pro branch inside it would
// recurse; and it opens with a stack of hooks including polling, which a non-Pro visitor
// seeing only a promo shouldn't be running.
export default function QuickCapturePanel(props) {
  const {
    autoSubmitInitialText, collectionsUrl, demoEntries, entriesUrl, importsUrl, initialText,
    isPro, isSignedIn, signInUrl, signUpUrl, targetLanguageEnglishName, targetLanguageIso,
    targetLanguageName
  } = props;

  if (isPro) {
    return (
      <QuickCapture
        autoSubmitInitialText={autoSubmitInitialText}
        collectionsUrl={collectionsUrl}
        entriesUrl={entriesUrl}
        importsUrl={importsUrl}
        initialText={initialText}
        targetLanguageIso={targetLanguageIso}
        targetLanguageName={targetLanguageName}
      />
    );
  }

  // Declared before the branches below, both of which use them.
  //
  // The promo reads in the user's base language, so use the English name ("Spanish"), not
  // the language's own name for itself ("Español").
  const promoLanguageName = targetLanguageEnglishName || targetLanguageName;

  // No cached sentences for this pairing yet - fall back to copy only.
  const hasDemo = !!(demoEntries && demoEntries.length);

  // Anonymous visitors get a different ask entirely. Selling a paid tier to someone who
  // doesn't yet know what Clozemaster is skips four rungs of the ladder — and on a page
  // built to be shared, a paid upsell above the fold is what gets a link removed. The
  // same reasoning FrenchNounArticles/SignUpPrompt already states: the only thing worth
  // selling a stranger is the free account, because that's the one that's true and
  // immediate.
  //
  // Requires the URLs, so callers that don't supply them (the in-app modal, reached from
  // the dashboard) keep the existing behaviour rather than rendering a broken card.
  if (isSignedIn === false && signUpUrl) {
    return (
      <>
        <div className="quick-capture-signup">
          <p className="quick-capture-signup-lead">Save the words you don&apos;t know</p>
          <p>
            Clozemaster turns words you meet in the wild into{" "}
            {promoLanguageName || "target language"} sentences you can practice, so they stick
            instead of sitting in a note you never reopen.
          </p>
          <p>
            {/* joystix, like every other call to action in the app - it's also the only
                thing on this panel that says "Clozemaster" to someone who arrived cold. */}
            <a className="btn btn-success joystix" href={signUpUrl}>
              Sign up free
            </a>
            {signInUrl && (
              <>
                {" "}
                <a className="quick-capture-signup-login" href={signInUrl}>
                  or log in
                </a>
              </>
            )}
          </p>
        </div>

        {/* The demo for anonymous visitors too. It's scripted and inert - no API calls, no
            LLM cost - so showing what capture actually does is free, and showing beats
            describing for someone who has never heard of the product. */}
        {hasDemo && (
          <div className="quick-capture-demo">
            <hr />
            <p className="text-muted">Here&apos;s what it looks like:</p>
            <QuickCapture
              demoEntries={demoEntries}
              targetLanguageEnglishName={promoLanguageName}
              targetLanguageIso={targetLanguageIso}
              targetLanguageName={targetLanguageName}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <ModalProPromo
        primary={
          <>
            See a new word or phrase somewhere, write it down, and never look at it again?
            {" "}
            Use Quick Capture with <span className="joystix">Clozemaster Pro</span> to turn it
            into a sentence in {promoLanguageName || "your target language"} you can play!
            Enter the word or phrase, get an example sentence and translation, save them to a
            collection.
          </>
        }
        secondary="Learn the words from your life, not just a word list."
      />
      {hasDemo && (
        <div className="quick-capture-demo" style={{ marginTop: 25 }}>
          <hr />
          {/* The demo below is inert, so say so - otherwise a read-only input under a
              sales pitch reads as broken. */}
          <p className="text-muted" style={{ marginBottom: 15 }}>
            Demo - here&apos;s what it looks like:
          </p>
          <QuickCapture
            demoEntries={demoEntries}
            targetLanguageEnglishName={promoLanguageName}
            targetLanguageIso={targetLanguageIso}
            targetLanguageName={targetLanguageName}
          />
        </div>
      )}
    </>
  );
}
